import { useState, useEffect, Fragment, useCallback, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
    ViewListingContainer,
    ViewListingExapndableDesktopWrapper,
    ViewListingTopContainer,
    ViewListingTopContainerWrapper,
    ViewListingThingsToKnowWrapper,
    ViewListingThingsToKnow,
    ViewListingPageContainer,
    DesktopHeaderContainer,
    DesktopHeaderLeft,
    DesktopHeaderRight,
    DesktopHeaderButton,
} from "./viewListingStyling";
import { userModelSelector } from "../../state/selectors/userModel";
import { accessTokenSelector } from "../../state/selectors/accessToken";
import { signInMenuActivatedSelector } from "../../state/selectors/navbarToggle";
import { useRecoilState } from "recoil";
import Footer from "../../components/footer/footer";
import { callApi } from "../../utils/api/api";
import { useNavigate } from "react-router-dom";
import useIsDesktopSize from "../../hook/useIsDesktopSize";
import constructOptimisedProtectedImageUrl from "../../utils/imageOptimisation/constructOptimisedProtectedImageUrl";
import constructOptimisedImageUrl from "../../utils/imageOptimisation/constructOptimisedImageUrl";
import { resolveListingImageSrc } from "../../utils/resolveListingImageSrc";
import { ColorTheme } from "../../components/globaStyledComponents/globalStyledComponentsStyling";
import { formatDateForBackend } from "../../utils/dateUtils";
import { useToast } from "../../components/toast/ToastContext";

// Import sections
import ViewListingDesktopHeader from "./sections/viewListingDesktopHeader";
import ViewListingTitle from "./sections/viewListingTitle";
import ViewListingDescription from "./sections/viewListingDescription";
import ViewListingAvailableRooms from "./sections/viewListingRooms";
import ViewListingHotelRooms from "./sections/viewListingHotelRooms";
import { isHotel } from "../../utils/listingUtils";
import ViewListingAmenitiesList from "./sections/viewListingAmenitiesList";
import ViewListingReviews from "./sections/viewListingsReviews";
import ViewListingAboutLocation from "./sections/viewListingAboutLocation";
import ViewListingExpandableSection from "./sections/viewListingDeposit";
import ViewListingHouseRulesSection from "./sections/viewListingHouseRules";
import ViewListingExpandableCancellationSection from "./sections/viewListingCancellation";
import ViewListingOwnerSection from "./sections/viewListingOwner";
import ViewListingFooter from "./sections/viewListingFooter";
import ViewListingNoServiceFee from "./sections/viewListingNoServiceFee";
import ViewListingHighlights from "./sections/ViewListingHighlights";
import ViewListingHostInfo from "./sections/viewListingHostInfo";
import ViewListingConcierge from "./sections/viewListingConcierge";
import ViewListingExtras from "./sections/viewListingExtras";

// Import skeleton
import SkeletonViewListingPage from "./skeletonViewListingPage/skeletonViewListingPage";

// Import popups
import DatepickerBooking from "../../components/datepicker/DatepickerBooking";
import SendMessageRequestPopup from "./popups/viewListingSendMessage";
import Analytics from "analytics";
import segmentPlugin from "@analytics/segment";
import GlobalBookingGuestPicker from "../../components/globalBookingGuestPicker/GlobalBookingGuestPicker";
import ReservationDetails from "./sections/viewListingReservationDetails";
import ShareListingPopup from "./popups/viewListingShare";
import ReviewPopup from "./popups/viewListingReviewPopup";
import { toggleSaved } from "../../utils/savedListings/toggleSaved";
import { ListingModel } from "../../interfaces/models/listingModel";
import { UnifiedListingModel } from "../../interfaces/models/UnifiedListingModel";
import { BookingDetail } from "../../interfaces/models/bookingDetail";
import { UserModel } from "../../interfaces/models/userModel";
import { identityIdSelector } from "../../state/selectors/identityId";
import { loggedInSelector } from "../../state/selectors/loggedIn";
import HelmetTags from "../../components/Helmet/HelmetTags";

import ViewListingCarousel from "./sections/ViewListingCarousel";
import ViewListingNotFound from "./popups/viewListingNotFound";
import ViewListingLightbox from './popups/ViewListingLightbox';

const analytics = Analytics({
    app: "Anystay",
    plugins: [
        segmentPlugin({
            writeKey: process.env.REACT_APP_SEGMENT_WRITE_KEY,
        }),
    ],
});

// Using the UnifiedListingModel interface instead of ListingInterface
export type ListingInterface = UnifiedListingModel;

export interface GuestsInterface {
    adults: number;
    children: number;
    infants: number;
    pets: number;
}

interface DatesInterface {
    startDate: Date | string;
    endDate: Date | string;
}

interface QueryParams {
    arrivalDate?: string;
    departureDate?: string;
    adults?: string;
    children?: string;
    infants?: string;
    pets?: string;
}

interface HotelImage {
    description: string;
    url: string;
    isMain: boolean;
}

const parseListingImages = (images: Array<string | HotelImage | ListingModel['listingImages'][0]>): string[] => {
    return images
        .map((img): string => {
            // Handle hotel images which have a url property
            if (typeof img === 'object' && 'url' in img) {
                return img.url;
            }
            
            // Handle regular listing images
            let key;
            if (typeof img === 'string') {
                try {
                    key = JSON.parse(img)?.key;
                } catch {
                    key = img;
                }
            } else {
                key = (img as { key: string }).key;
            }
            return constructOptimisedImageUrl(key, 1500);
        });
};

export default function ViewListing() {
    const { id } = useParams();
    const [userModel] = useRecoilState(userModelSelector);
    const [identityId] = useRecoilState(identityIdSelector);
    const [loggedIn] = useRecoilState(loggedInSelector);
    const [, setSignInMenuActivated] = useRecoilState(signInMenuActivatedSelector);
    const [accessToken] = useRecoilState(accessTokenSelector);
    const navigate = useNavigate();
    const isDesktopSize = useIsDesktopSize();
    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const { showToast } = useToast();

    //get query params
    const location = useLocation();
    const queryParams = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const params: QueryParams = {};
        for (let [key, value] of searchParams.entries()) {
            params[key as keyof QueryParams] = value;
        }
        return params;
    }, [location.search]);

    //picker state
    const [dates, setDates] = useState({
        startDate: queryParams.arrivalDate ? new Date(`${queryParams.arrivalDate.split("-")[2]}-${queryParams.arrivalDate.split("-")[1]}-${queryParams.arrivalDate.split("-")[0]}`) : "anyArrival",
        endDate: queryParams.departureDate
            ? new Date(`${queryParams.departureDate.split("-")[2]}-${queryParams.departureDate.split("-")[1]}-${queryParams.departureDate.split("-")[0]}`)
            : "anyDeparture",
    });
    const [guests, setGuests] = useState<GuestsInterface>({
        adults: queryParams.adults && Number(queryParams.adults) ? Number(queryParams.adults) : 1,
        children: queryParams.children && Number(queryParams.children) ? Number(queryParams.children) : 0,
        infants: queryParams.infants && Number(queryParams.infants) ? Number(queryParams.infants) : 0,
        pets: queryParams.pets && Number(queryParams.pets) ? Number(queryParams.pets) : 0,
    });

    //data
    const [listing, setListing] = useState<ListingInterface | null>(null);
    const [listingOwnerData, setListingOwnerData] = useState<UserModel>({ firstName: '', id: '' } as UserModel);
    const [listingOwnerProfilePictureURL, setListingOwnerProfilePictureURL] = useState<string>("");
    const [bookingDetails, setBookingDetails] = useState<BookingDetail | null>(null);

    //popup state
    const [calendarSelected, setCalendarSelected] = useState(false);
    const [shareListingSelected, setShareListingSelected] = useState(false);
    const [sendMessage, setSendMessage] = useState(false);
    const [isReview, setIsReview] = useState(false);
    const [reservervationGuestsMenu, setReservationGuestsMenu] = useState(false);
    const [nightlyPriceBreakdownPopupActive, setNightlyPriceBreakdownPopupActive] = useState(false);
    //page state
    const [sendAMessageBtnClicked, setSendAMessageBtnClicked] = useState(false);
    //checkout window size
    const [isNotDesktopSize, setisNotDesktopSize] = useState<boolean>(window.innerWidth < 1024);

    const [isSaved, setIsSaved] = useState(false);
    const reviewsRef = useRef<HTMLDivElement>(null);
    const [listingNotFound, setListingNotFound] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const isSavedLoading = useRef(false);

    // Removed debug log for isScrolled state changes
    useEffect(() => {
    }, [isScrolled]);

    // Add this computed value
    const parsedImages = useMemo(() => {
        if (!listing?.images) return [];
        return parseListingImages(listing.images);
    }, [listing?.images]);

    // Calendar data
    const [calendarObjectsRaw, setCalendarObjectsRaw] = useState<any[]>([]);

    // Fetch calendar using the new calendar API
    useEffect(() => {
        const fetchCalendarUnavailableDates = async () => {
            if (!accessToken || !id) return; // Don't fetch if we don't have the required data
            if (isHotel(id)) {
                return; // Skip calendar fetch for hotel mode
            }

            try {
                const today = new Date();
                const endDate = new Date();
                endDate.setFullYear(today.getFullYear() + 1);

                const response = await callApi("get", `${process.env.REACT_APP_ENV}-anystay-calendar`, `calendars?query=dayPicker&listingModelId=${id}&startDate=${today.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}&timeZoneCode=Australia/Brisbane`, {
                    headers: {
                        Authorization: accessToken,
                    },
                });

                if (response?.data?.calendarObjects) {
                    const calendarObjects = response.data.calendarObjects;
                    setCalendarObjectsRaw(calendarObjects);
                }
            } catch (error) {
                console.error("Error fetching calendar data:", error);
            }
        };

        fetchCalendarUnavailableDates();
    }, [id, accessToken]);

    useEffect(() => {
        const handleResize = (): void => {
            setisNotDesktopSize(window.innerWidth < 1024);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    //disable scroll when popup is active
    useEffect(() => {
        if (calendarSelected || shareListingSelected || sendMessage || reservervationGuestsMenu || isReview) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [calendarSelected, shareListingSelected, sendMessage, reservervationGuestsMenu, isReview]);

    useEffect(() => {
        if (userModel && userModel.savedListings.includes(listing ? listing.id : "")) {
            setIsSaved(true);
        }
        setTimeout(() => {
            if (listing && userModel) {
                analytics.track("viewListingPageView", {
                    userId: loggedIn ? userModel?.username : `anon_${identityId}`,
                    listingId: id,
                    propertyTitle: listing.propertyTitle,
                    queryParams: queryParams,
                });
            } else {
                analytics.track("viewListingPageView", {
                    userId: loggedIn ? userModel?.username : `anon_${identityId}`,
                    listingId: id,
                    propertyTitle: listing?.propertyTitle,
                    queryParams: queryParams,
                });
            }
        }, 1200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, listing, userModel]);

    const calculatePrice = useCallback(async (dates: DatesInterface, guests: GuestsInterface, listing: ListingInterface) => {
        // Use different endpoint for hotels
        if (isHotel(listing.id)) {
            console.log('🏨 Making hotel quote API call for listing:', listing.id);
            console.log('📅 Dates:', { startDate: dates.startDate, endDate: dates.endDate });
            console.log('👥 Guests:', guests);
            
            try {
                let res = await callApi("post", `${process.env.REACT_APP_ENV}-anystay-etg`, `hotel/quote`, {
                    headers: {
                        Authorization: accessToken,
                    },
                    body: {
                        listingModelId: listing.id,
                        arrivalDate: formatDateForBackend(dates.startDate),
                        departureDate: formatDateForBackend(dates.endDate),
                        adults: guests.adults,
                        children: guests.children,
                        infants: guests.infants,
                        pets: guests.pets,
                    },
                });
                
                console.log('🏨 Hotel quote API response:', res);

                // Check for DATE_NOT_AVAILABLE error
                if (!res.success && res.errorMessage?.code === 301 && res.errorMessage?.name === "DATE_NOT_AVAILABLE") {
                    console.log('❌ Dates not available:', res.errorMessage);
                    showToast("Dates unavailable. Please select different dates.", 'error');
                    setBookingDetails(null);
                    return;
                }

                // Extract all roomDetails from all rates in the response
                const allRoomDetails = res.data?.rates?.map((rate: any) => rate.roomDetails).filter(Boolean) || [];
                
                console.log('🔍 Extracted all roomDetails:', allRoomDetails);

                // Set bookingDetails regardless of success check since we have the data
                if (res.data) {
                    console.log('🏨 Hotel quote response:', res.data);
                    
                    // Get guestBreakdown from the first rate since it's not at the top level
                    const firstRate = res.data.rates && res.data.rates.length > 0 ? res.data.rates[0] : null;
                    const guestBreakdown = firstRate?.guestBreakdown;
                    
                    const nights = guestBreakdown?.priceItems[0]?.label.includes("nights") ? 
                        parseInt(guestBreakdown.priceItems[0].label) : 1;
                    
                    // Create a proper guestBreakdown structure that matches what components expect
                    const formattedGuestBreakdown = {
                        priceItems: guestBreakdown?.priceItems || [],
                        priceDiffItems: guestBreakdown?.priceDiffItems || [],
                        subtotalItems: guestBreakdown?.subtotalItems || []
                    };
                    
                    const bookingDetails: BookingDetail = {
                        ...res.data,
                        guestBreakdown: formattedGuestBreakdown,  // Use the formatted structure
                        displayItems: formattedGuestBreakdown,  // For backward compatibility
                        pricePerNight: firstRate?.paymentAmount ? firstRate.paymentAmount / nights : 0,
                        paymentAmount: firstRate?.paymentAmount || 0,
                        hotelRates: res.data.rates || [], // Use rates instead of hotelRates
                        roomDetails: allRoomDetails  // Store all roomDetails for matching
                    };
                    
                    console.log('💾 Setting bookingDetails with roomDetails:', {
                        roomDetails: bookingDetails.roomDetails,
                        hasRoomDetails: !!bookingDetails.roomDetails
                    });
                    
                    setBookingDetails(bookingDetails);
                } else {
                    console.log('❌ Hotel quote API failed:', res);
                    showToast("Unable to get pricing. Please try again.", 'error');
                    setBookingDetails(null);
                }
            } catch (error) {
                console.error('❌ Hotel quote API error:', error);
                showToast("Unable to get pricing. Please try again.", 'error');
                setBookingDetails(null);
            }
            return;
        }

        // Regular listing endpoint
        try {
            let res = await callApi("post", `${process.env.REACT_APP_ENV}-anystay-booking`, `booking/quote`, {
                headers: {
                    Authorization: accessToken,
                },
                body: {
                    listingModelId: listing.id,
                    arrivalDate: formatDateForBackend(dates.startDate),
                    departureDate: formatDateForBackend(dates.endDate),
                    adults: guests.adults,
                    children: guests.children,
                    infants: guests.infants,
                    pets: guests.pets,
                },
            });

            // Check for DATE_NOT_AVAILABLE error
            if (!res.success && res.errorMessage?.code === 301 && res.errorMessage?.name === "DATE_NOT_AVAILABLE") {
                console.log('❌ Dates not available:', res.errorMessage);
                showToast("Dates unavailable. Please select different dates.", 'error');
                setBookingDetails(null);
                return;
            }

            if (res.success) {
                const nights = res.data.guestBreakdown.priceItems[0]?.label.includes("nights") ? 
                    parseInt(res.data.guestBreakdown.priceItems[0].label) : 1;
                
                const bookingDetails: BookingDetail = {
                    ...res.data,
                    displayItems: res.data.guestBreakdown,  // For backward compatibility
                    pricePerNight: res.data.paymentAmount / nights
                };
                setBookingDetails(bookingDetails);
            } else {
                console.log('❌ Booking quote API failed:', res);
                showToast("Unable to get pricing. Please try again.", 'error');
                setBookingDetails(null);
            }
        } catch (error) {
            console.error('❌ Booking quote API error:', error);
            showToast("Unable to get pricing. Please try again.", 'error');
            setBookingDetails(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);
    const initListing = useCallback(
        async (id: string, dates: DatesInterface, guests: GuestsInterface) => {
            try {
                let response = await callApi("get", `${process.env.REACT_APP_ENV}-anystay-listing`, `listing/unified/${id}`, {
                    headers: {
                        Authorization: accessToken,
                    },
                });
                if (!response.data?.unifiedListing) {
                    setListingNotFound(true);
                    return;
                }
                const listing = response.data.unifiedListing;
                setListing(listing);
                if (listing.UserHostModel?.UserModel?.profilePicture) {
                    const listingOwnerProfilePic = constructOptimisedProtectedImageUrl(listing.UserHostModel.UserModel.profilePicture, listing.UserHostModel.UserModel.identityID, 100);
                    setListingOwnerProfilePictureURL(listingOwnerProfilePic);
                } else {
                    setListingOwnerProfilePictureURL("");
                }
                setListingOwnerData(listing.UserHostModel?.UserModel || { firstName: '', id: '' });
                if (dates.startDate !== "anyArrival" && dates.endDate !== "anyDeparture") {
                    await calculatePrice(dates, guests, listing);
                }
            } catch (err) {
                console.log(err);
                setListingNotFound(true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [calculatePrice]
    );
    //init data
    useEffect(() => {
        const init = async () => {
            await initListing(id as string, dates, guests);
        };
        init();
    }, [id, initListing]);

    // Calculate pricing when page loads with URL parameters
    useEffect(() => {
        const calculatePricingOnLoad = async () => {
            if (listing && dates.startDate !== "anyArrival" && dates.endDate !== "anyDeparture") {
                console.log('🔄 Calculating pricing on page load with URL parameters:', {
                    dates,
                    guests,
                    listingId: listing.id
                });
                await calculatePrice(dates, guests, listing);
            }
        };
        calculatePricingOnLoad();
    }, [listing, dates, guests, calculatePrice]);

    const updateDates = async (date: Date[]) => {
        if (!listing) return;
        await calculatePrice({ startDate: date[0], endDate: date[1] }, guests, listing);
        setDates({ startDate: date[0], endDate: date[1] });
        
        // Update URL with new dates
        const arrivalDate = `${String(date[0].getDate()).padStart(2, "0")}-${String(date[0].getMonth() + 1).padStart(2, "0")}-${date[0].getFullYear()}`;
        const departureDate = `${String(date[1].getDate()).padStart(2, "0")}-${String(date[1].getMonth() + 1).padStart(2, "0")}-${date[1].getFullYear()}`;
        
        const arrivalDateString = `arrivalDate=${arrivalDate}`;
        const departureDateString = `departureDate=${departureDate}`;
        const adultsString = guests.adults ? `adults=${guests.adults}` : "";
        const childrenString = guests.children ? `children=${guests.children}` : "";
        const petsString = guests.pets ? `pets=${guests.pets}` : "";
        const infantsString = guests.infants ? `infants=${guests.infants}` : "";
        const searchParamsStrings = [arrivalDateString, departureDateString, adultsString, childrenString, petsString, infantsString].filter((param) => param !== "").join("&");
        
        const newUrl = `/listing/${id}${searchParamsStrings ? `?${searchParamsStrings}` : ""}`;
        navigate(newUrl, { replace: true });
        
        analytics.track("ViewListingUpdateDates", {
            userId: loggedIn ? userModel?.username : `anon_${identityId}`,
            listingId: id,
            propertyTitle: listing?.propertyTitle,
            arrivalDate: date[0],
            departureDate: date[1],
        });
    };
    const updateGuests = async (guests: { adults: number; children: number; infants: number; pets: number }) => {
        if (!listing) return;
        if (dates.startDate !== "anyArrival" && dates.endDate !== "anyDeparture") {
            await calculatePrice(dates, guests, listing);
        }
        setGuests({
            adults: guests.adults,
            children: guests.children,
            infants: guests.infants,
            pets: guests.pets,
        });
        
        // Update URL with new guest parameters
        if (dates.startDate !== "anyArrival" && dates.endDate !== "anyDeparture") {
            const arrivalDate = `${String((dates.startDate as Date).getDate()).padStart(2, "0")}-${String((dates.startDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.startDate as Date).getFullYear()}`;
            const departureDate = `${String((dates.endDate as Date).getDate()).padStart(2, "0")}-${String((dates.endDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.endDate as Date).getFullYear()}`;
            
            const arrivalDateString = `arrivalDate=${arrivalDate}`;
            const departureDateString = `departureDate=${departureDate}`;
            const adultsString = guests.adults ? `adults=${guests.adults}` : "";
            const childrenString = guests.children ? `children=${guests.children}` : "";
            const petsString = guests.pets ? `pets=${guests.pets}` : "";
            const infantsString = guests.infants ? `infants=${guests.infants}` : "";
            const searchParamsStrings = [arrivalDateString, departureDateString, adultsString, childrenString, petsString, infantsString].filter((param) => param !== "").join("&");
            
            const newUrl = `/listing/${id}${searchParamsStrings ? `?${searchParamsStrings}` : ""}`;
            navigate(newUrl, { replace: true });
        }
        
        analytics.track("ViewListingUpdateGuests", {
            userId: loggedIn ? userModel?.username : `anon_${identityId}`,
            listingId: id,
            propertyTitle: listing?.propertyTitle,
            guests: guests.adults + guests.children,
            adults: guests.adults,
            children: guests.children,
            infants: guests.infants,
            pets: guests.pets,
        });
    };

    const toggleCalendar = () => {
        setCalendarSelected(!calendarSelected);
    };
    const toggleGuests = () => {
        setReservationGuestsMenu(!reservervationGuestsMenu);
    };

    const toggleSendMessageRequest = async () => {
        if (userModel) {
            setSendMessage(!sendMessage);
        } else {
            setSignInMenuActivated(true);
        }
    };
    const toggleSeeReview = async () => {
        setIsReview(!isReview);
        analytics.track("ViewListingSeeAllReviewsOpened", {
            userId: loggedIn ? userModel?.username : `anon_${identityId}`,
            listingId: id,
            propertyTitle: listing?.propertyTitle,
        });
    };
    const toggleSendAMessageBtnClicked = async () => {
        setSendAMessageBtnClicked(!sendAMessageBtnClicked);
    };
    const toggleNightlyPriceBreakdownPopup = () => {
        setNightlyPriceBreakdownPopupActive(!nightlyPriceBreakdownPopupActive);
    };

    const toggleShareListing = async () => {
        setShareListingSelected(!shareListingSelected);
        analytics.track("ViewListingShareListingPopupOpened", {
            userId: loggedIn ? userModel?.username : `anon_${identityId}`,
            listingId: id,
            propertyTitle: listing?.propertyTitle,
        });
    };



    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Check initial scroll position immediately
    useEffect(() => {
        const container = contentWrapperRef.current;
        if (container) {
            const scrollTop = container.scrollTop;
            const isCurrentlyScrolled = scrollTop > 0;
            setIsScrolled(isCurrentlyScrolled);
        }
    }, []);

    // Add scroll detection
    useEffect(() => {
        const container = contentWrapperRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const isCurrentlyScrolled = scrollTop > 0;
            setIsScrolled(isCurrentlyScrolled);
        };

        // Add the scroll listener
        container.addEventListener('scroll', handleScroll);
        
        return () => container.removeEventListener('scroll', handleScroll);
    }, [contentWrapperRef.current]);

    // Removed debug isScrolled state changes
    useEffect(() => {
    }, [isScrolled]);

    const handleShare = async () => {
        if (navigator.share && listing) {
            try {
                const title = listing.propertyTitle || "Anystay";
                const location = [listing.suburb || listing.city, listing.stateShort || listing.state, listing.country].filter(Boolean).join(", ");
                const detailsParts = [
                    listing.propertyType,
                    location ? `in ${location}` : undefined,
                    typeof listing.bedrooms === 'number' ? `${listing.bedrooms} bedroom${listing.bedrooms === 1 ? '' : 's'}` : undefined,
                    typeof listing.bathrooms === 'number' ? `${listing.bathrooms} bathroom${listing.bathrooms === 1 ? '' : 's'}` : undefined,
                ].filter(Boolean);
                const text = detailsParts.join(" · ");
                await navigator.share({
                    title,
                    text,
                    url: typeof window !== 'undefined' ? window.location.href : `https://anystay.com/listing/${listing.id}`,
                });
            } catch (error) {
                console.error("Error sharing:", error);
            }
        } else {
            alert("Web Share is not supported in your browser.");
        }
    };



    const sendMessageEvent = (listingId: any, startDate: any, endDate: any, message: any) => {
        analytics.track("SentMessageFromViewListing", {
            userId: loggedIn ? userModel?.id : identityId,
            listingId: listingId,
            startDate: startDate,
            endDate: endDate,
            message: message,
        });
    };



    const handleImageClick = (index: number) => {
        setPhotoIndex(index);
        setLightboxOpen(true);
    };

    const handleRoomImageClick = (imageIndex: number, roomImages: string[]) => {
        // Convert room images to optimized URLs and add them to the main lightbox
        const optimizedRoomImages = roomImages.map(img => constructOptimisedImageUrl(img, 1000));
        
        // For now, we'll just open the first room image
        // In the future, we could merge all room images into the main carousel
        setPhotoIndex(0);
        setLightboxOpen(true);
    };

    if (listingNotFound) {
        return <ViewListingNotFound closePopup={() => setListingNotFound(false)} />;
    }

    if (!listing) {
        return <SkeletonViewListingPage />;
    }

    return (
        <>
            <HelmetTags 
                title={listing ? `${listing.propertyTitle} | ${listing.propertyType} in ${listing.suburb}, ${listing.state}, ${listing.country} | Anystay` : "Anystay | Short & Long Stays, Holiday Homes, Rooms & More"}
                description={listing
                    ? `${listing.propertyType} in ${listing.suburb}, ${listing.state}. ${listing.bedrooms} bedrooms, ${listing.bathrooms} bathrooms. Book your stay with Anystay.`
                    : "Book the perfect Australian stay at the best prices with Anystay. Find amazing deals on the biggest range of accommodation in Australia."}
                imgUrl={(() => {
                    const fallback = "https://d292awxalydr86.cloudfront.net/General/Stays+in+Australia.jpg";
                    try {
                        if (!listing || !listing.images || !listing.images[0]) return fallback;
                        return resolveListingImageSrc(listing.images[0] as any, 100) || fallback;
                    } catch {
                        return fallback;
                    }
                })()}
                url={typeof window !== 'undefined' ? window.location.href : (listing ? `https://anystay.com/listing/${listing.id}` : "https://www.anystay.com/")}
            />
            <DesktopHeaderContainer isScrolled={isScrolled} lightboxOpen={lightboxOpen}>
                <DesktopHeaderLeft onClick={() => navigate('/')}>
                    <img src="https://d292awxalydr86.cloudfront.net/Branding/Anystay.svg" alt="Anystay" />
                </DesktopHeaderLeft>
                <DesktopHeaderRight>
                    <DesktopHeaderButton
                        onClick={handleShare}
                        disabled={!listing}
                    >
                        <img src="https://d292awxalydr86.cloudfront.net/Universal+icons/share.svg" alt="Share" />
                    </DesktopHeaderButton>
                    <DesktopHeaderButton
                        onClick={async () => {
                            if (userModel) {
                                if (isSavedLoading.current) return;
                                try {
                                    setIsSaved((prev) => !prev);
                                    isSavedLoading.current = true;
                                    await toggleSaved(listing.id, userModel, accessToken);
                                } catch (err: any) {
                                    console.log(err);
                                    setIsSaved((prev) => !prev);
                                } finally {
                                    isSavedLoading.current = false;
                                }
                            } else {
                                setSignInMenuActivated(true);
                            }
                        }}
                        disabled={!listing}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
                            <path
                                d="M2.26061 13.3918C5.07331 17.4376 8.89663 20.4197 12.8238 23.2792C12.9722 23.3873 13.373 23.3224 13.5518 23.1927C16.4106 21.1187 19.1902 18.9466 21.5963 16.3436C23.5642 14.2148 25.3072 11.9361 26.0237 9.04917C26.8138 5.86965 25.4788 2.5114 22.8246 0.914431C20.2484 -0.634975 16.9311 -0.189611 14.4629 2.05451L14.4198 2.09405C14.1243 2.36677 13.8509 2.66287 13.5765 2.96004C13.443 3.10465 13.3093 3.2495 13.1726 3.39204C13.0119 3.23609 12.8591 3.08852 12.7119 2.94629C12.4578 2.7008 12.2197 2.47076 11.9833 2.23899C11.949 2.20545 11.9146 2.17227 11.8799 2.13945C9.70682 0.0822306 6.68254 -0.554266 4.17227 0.529602C1.6306 1.62644 -0.0576046 4.36492 0.00150402 7.35708C0.0461959 9.62714 1.00058 11.5816 2.26061 13.3918Z"
                                fill={isSaved ? `${ColorTheme.Red.color}` : "none"}
                                stroke={isSaved ? `${ColorTheme.Red.color}` : `${ColorTheme.Black.color}`}
                                strokeWidth="1.8"
                                transform="translate(0.7, 2.5)"
                            />
                        </svg>
                    </DesktopHeaderButton>
                </DesktopHeaderRight>
            </DesktopHeaderContainer>
            <div style={{ background: '#fafafa' }}>
                {window.innerWidth >= 768 ? (
                    <>
                        {window.innerWidth >= 1024 && (
                            <ViewListingDesktopHeader
                                handleShare={handleShare}
                                toggleShareListing={toggleShareListing}
                                dates={dates}
                                guests={guests}
                                bookingDetails={bookingDetails}
                                listing={listing}
                                accessToken={accessToken}
                                toggleCalendar={toggleCalendar}
                                toggleSaved={toggleSaved}
                                isSaved={isSaved ? isSaved : false}
                                setIsSaved={setIsSaved}
                                reviewsRef={reviewsRef}
                                contentWrapperRef={contentWrapperRef}
                                calendarSelected={calendarSelected}
                                userModel={userModel}
                            />
                        )}
                        <ViewListingPageContainer isScrolled={isScrolled} ref={contentWrapperRef}>
                                <ViewListingCarousel
                                    eventOnImageClick={handleImageClick}
                                    images={parsedImages}
                                    listingId={listing?.id || ''}
                                    listingTitle={listing?.propertyTitle || ''}
                                    propertyDetails={{
                                        guests: listing?.guests || 0,
                                        bedrooms: listing?.bedrooms || 0,
                                        beds: listing?.beds || 0,
                                        baths: listing?.bathrooms || 0,
                                        area: listing?.areaValue || 0,
                                    }}
                                    isSaved={isSaved}
                                    setIsSaved={setIsSaved}
                                    toggleShareListing={toggleShareListing}
                                />
                                <ViewListingContainer>
                                    <ViewListingTopContainerWrapper>
                                        <ViewListingTopContainer>
                                            <ViewListingTitle
                                                onReviewClick={toggleSeeReview}
                                                propertyTitle={listing.propertyTitle}
                                                propertyType={listing.propertyType}
                                                suburb={listing.city}
                                                state={listing.stateShort}
                                                listing={{...listing, ReviewStayModels: listing.ReviewStayModels || { items: [] }}}
                                                hotelRooms={listing.hotelRooms}
                                                typeOfPlace={listing.typeOfPlace}
                                            />

                                            {/* Removing verified listing section */}
                                            {/* <ViewListingVerifyListing /> */}

                                            {!isHotel(listing.id) && (
                                                <ViewListingHostInfo listingOwnerProfilePictureURL={listingOwnerProfilePictureURL} listingOwnerData={listingOwnerData} />
                                            )}
                                            <ViewListingConcierge toggleSendMessageRequest={toggleSendMessageRequest} />
                                            <ViewListingNoServiceFee />
                                            <ViewListingHighlights listing={listing} reviews={listing.ReviewStayModels?.items || []} userModel={userModel} />
                                            <ViewListingDescription description={listing.description ? listing.description : ""} />
                                                                                {!isHotel(listing.id) && (
                                        <ViewListingAvailableRooms 
                                            availableRooms={listing.listingRooms || []} 
                                            amenities={listing.amenities}
                                            onRoomImageClick={handleRoomImageClick}
                                        />
                                    )}
                                            <ViewListingAmenitiesList title={"Included at your place"} amenities={listing.amenities} cardQty={10} />
                                        </ViewListingTopContainer>
                                        <ReservationDetails
                                            dates={dates}
                                            guests={guests}
                                            bookingDetails={bookingDetails}
                                            listing={listing}
                                            toggleCalendar={toggleCalendar}
                                            userModel={userModel}
                                            toggleGuests={toggleGuests}
                                            toggleNightlyPriceBreakdownPopup={toggleNightlyPriceBreakdownPopup}
                                            nightlyPriceBreakdownPopupActive={nightlyPriceBreakdownPopupActive}
                                        />
                                    </ViewListingTopContainerWrapper>

                                    <ViewListingExtras listingId={listing.id} accessToken={accessToken} userModel={userModel} />

                                    {isHotel(listing.id) && listing.hotelRooms?.length && (
                                        <ViewListingHotelRooms 
                                            hotelRooms={listing.hotelRooms || []} 
                                            hotelRates={bookingDetails?.hotelRates || []}
                                            roomDetails={bookingDetails?.roomDetails}
                                            onRoomImageClick={handleRoomImageClick}
                                            onReserveRoom={() => toggleCalendar()}
                                            userModel={userModel}
                                            listing={listing}
                                            dates={dates}
                                            guests={guests}
                                        />
                                    )}

                                    {!isHotel(listing.id) && (
                                        <ViewListingReviews listing={listing} reviews={listing.ReviewStayModels?.items || []} toggleSeeReview={toggleSeeReview} reviewsRef={reviewsRef} />
                                    )}

                                    <ViewListingAboutLocation 
                                        hasReviews={false} 
                                        listing={listing} 
                                        lat={typeof listing.coordinates === 'object' 
                                            ? (listing.coordinates as {lat: number; lon: number}).lat
                                            : ((() => {
                                                try {
                                                    return JSON.parse(listing.coordinates as string).lat;
                                                } catch {
                                                    return 0;
                                                }
                                            })())} 
                                        lng={typeof listing.coordinates === 'object'
                                            ? (listing.coordinates as {lat: number; lon: number}).lon
                                            : ((() => {
                                                try {
                                                    return JSON.parse(listing.coordinates as string).lon;
                                                } catch {
                                                    return 0;
                                                }
                                            })())}
                                    />

                                    <ViewListingOwnerSection
                                        listing={listing}
                                        dates={dates}
                                        listingOwnerData={listingOwnerData}
                                        listingOwnerProfilePictureURL={listingOwnerProfilePictureURL}
                                        toggleCalendar={toggleCalendar}
                                        toggleSendMessageRequest={toggleSendMessageRequest}
                                        toggleSendAMessageBtnClicked={toggleSendAMessageBtnClicked}
                                        sendAMessageBtnClicked={sendAMessageBtnClicked}
                                    />
                                    {isDesktopSize && (
                                        <ViewListingThingsToKnowWrapper>
                                            <ViewListingThingsToKnow>Things to know</ViewListingThingsToKnow>
                                        </ViewListingThingsToKnowWrapper>
                                    )}
                                    <ViewListingExapndableDesktopWrapper>
                                        <ViewListingHouseRulesSection title={"House rules"} checkOutTime={listing.checkOutTime} checkInTime={listing.checkInTime} points={listing.houseRules} />
                                        <ViewListingExpandableSection deposit={listing.deposit} />
                                        <ViewListingExpandableCancellationSection title={"Cancellations"} cancellationPolicy={listing.cancellationPolicy} listingId={listing.id} dates={dates} />
                                    </ViewListingExapndableDesktopWrapper>
                                    {/* {process.env.REACT_APP_ENV === "dev" && <ViewListingSimilarStaySection clickedListingFromRecommendation={clickedListingFromRecommendation} />} */}
                                </ViewListingContainer>
                            </ViewListingPageContainer>
                    </>
                ) : (
                    <ViewListingPageContainer isScrolled={isScrolled} ref={contentWrapperRef}>
                        {window.innerWidth >= 1024 && (
                            <ViewListingDesktopHeader
                                handleShare={handleShare}
                                toggleShareListing={toggleShareListing}
                                dates={dates}
                                guests={guests}
                                bookingDetails={bookingDetails}
                                listing={listing}
                                accessToken={accessToken}
                                toggleCalendar={toggleCalendar}
                                toggleSaved={toggleSaved}
                                isSaved={isSaved ? isSaved : false}
                                setIsSaved={setIsSaved}
                                reviewsRef={reviewsRef}
                                contentWrapperRef={contentWrapperRef}
                                calendarSelected={calendarSelected}
                                userModel={userModel}
                            />
                        )}

                        <ViewListingCarousel
                            eventOnImageClick={handleImageClick}
                            images={parsedImages}
                            listingId={listing?.id || ''}
                            listingTitle={listing?.propertyTitle || ''}
                            propertyDetails={{
                                guests: listing?.guests || 0,
                                bedrooms: listing?.bedrooms || 0,
                                beds: listing?.beds || 0,
                                baths: listing?.bathrooms || 0,
                                area: listing?.areaValue || 0,
                            }}
                            isSaved={isSaved}
                            setIsSaved={setIsSaved}
                            toggleShareListing={toggleShareListing}
                        />
                        <ViewListingContainer>
                            <ViewListingTopContainerWrapper>
                                <ViewListingTopContainer>
                                    <ViewListingTitle
                                        onReviewClick={toggleSeeReview}
                                        propertyTitle={listing.propertyTitle}
                                        propertyType={listing.propertyType}
                                        suburb={listing.city}
                                        state={listing.stateShort}
                                        listing={{...listing, ReviewStayModels: listing.ReviewStayModels || { items: [] }}}
                                        hotelRooms={listing.hotelRooms}
                                        typeOfPlace={listing.typeOfPlace}
                                    />

                                    {/* Removing verified listing section */}
                                    {/* <ViewListingVerifyListing /> */}

                                    {!isHotel(listing.id) && (
                                        <ViewListingHostInfo listingOwnerProfilePictureURL={listingOwnerProfilePictureURL} listingOwnerData={listingOwnerData} />
                                    )}
                                    <ViewListingConcierge toggleSendMessageRequest={toggleSendMessageRequest} />
                                    <ViewListingNoServiceFee />
                                    <ViewListingHighlights listing={listing} reviews={listing.ReviewStayModels?.items || []} userModel={userModel} />
                                    <ViewListingDescription description={listing.description ? listing.description : ""} />
                                                                        {!isHotel(listing.id) && (
                                        <ViewListingAvailableRooms 
                                            availableRooms={listing.listingRooms || []} 
                                            amenities={listing.amenities}
                                            onRoomImageClick={handleRoomImageClick}
                                        />
                                    )}
                                    <ViewListingAmenitiesList title={"Included at your place"} amenities={listing.amenities} cardQty={10} />
                                </ViewListingTopContainer>
                                <ReservationDetails
                                    dates={dates}
                                    guests={guests}
                                    bookingDetails={bookingDetails}
                                    listing={listing}
                                    toggleCalendar={toggleCalendar}
                                    userModel={userModel}
                                    toggleGuests={toggleGuests}
                                    toggleNightlyPriceBreakdownPopup={toggleNightlyPriceBreakdownPopup}
                                    nightlyPriceBreakdownPopupActive={nightlyPriceBreakdownPopupActive}
                                />
                            </ViewListingTopContainerWrapper>

                            <ViewListingExtras listingId={listing.id} accessToken={accessToken} userModel={userModel} />

                            {isHotel(listing.id) && listing.hotelRooms?.length && (
                                <ViewListingHotelRooms 
                                    hotelRooms={listing.hotelRooms || []} 
                                    hotelRates={bookingDetails?.hotelRates || []}
                                    roomDetails={bookingDetails?.roomDetails}
                                    onRoomImageClick={handleRoomImageClick}
                                    onReserveRoom={() => toggleCalendar()}
                                    userModel={userModel}
                                    listing={listing}
                                    dates={dates}
                                    guests={guests}
                                />
                            )}

                            {!isHotel(listing.id) && (
                                <ViewListingReviews listing={listing} reviews={listing.ReviewStayModels?.items || []} toggleSeeReview={toggleSeeReview} reviewsRef={reviewsRef} />
                            )}

                            <ViewListingAboutLocation 
                                hasReviews={false} 
                                listing={listing} 
                                lat={typeof listing.coordinates === 'object' 
                                    ? (listing.coordinates as {lat: number; lon: number}).lat
                                    : ((() => {
                                        try {
                                            return JSON.parse(listing.coordinates as string).lat;
                                        } catch {
                                            return 0;
                                        }
                                    })())} 
                                lng={typeof listing.coordinates === 'object'
                                    ? (listing.coordinates as {lat: number; lon: number}).lon
                                    : ((() => {
                                        try {
                                            return JSON.parse(listing.coordinates as string).lon;
                                        } catch {
                                            return 0;
                                        }
                                    })())}
                            />

                            <ViewListingOwnerSection
                                listing={listing}
                                dates={dates}
                                listingOwnerData={listingOwnerData}
                                listingOwnerProfilePictureURL={listingOwnerProfilePictureURL}
                                toggleCalendar={toggleCalendar}
                                toggleSendMessageRequest={toggleSendMessageRequest}
                                toggleSendAMessageBtnClicked={toggleSendAMessageBtnClicked}
                                sendAMessageBtnClicked={sendAMessageBtnClicked}
                            />
                            {isDesktopSize && (
                                <ViewListingThingsToKnowWrapper>
                                    <ViewListingThingsToKnow>Things to know</ViewListingThingsToKnow>
                                </ViewListingThingsToKnowWrapper>
                            )}
                            <ViewListingExapndableDesktopWrapper>
                                <ViewListingHouseRulesSection title={"House rules"} checkOutTime={listing.checkOutTime} checkInTime={listing.checkInTime} points={listing.houseRules} />
                                <ViewListingExpandableSection deposit={listing.deposit} />
                                <ViewListingExpandableCancellationSection title={"Cancellations"} cancellationPolicy={listing.cancellationPolicy} listingId={listing.id} dates={dates} />
                            </ViewListingExapndableDesktopWrapper>
                            {/* {process.env.REACT_APP_ENV === "dev" && <ViewListingSimilarStaySection clickedListingFromRecommendation={clickedListingFromRecommendation} />} */}
                        </ViewListingContainer>
                    </ViewListingPageContainer>
                )}
                {isNotDesktopSize && <ViewListingFooter toggleCalendar={toggleCalendar} dates={dates} guests={guests} listing={listing} bookingDetails={bookingDetails} userModel={userModel} />}
            </div>
            <Footer />
            {sendMessage && (
                <SendMessageRequestPopup
                    accessToken={accessToken}
                    listing={listing}
                    toggleSelected={toggleSendMessageRequest}
                    selected={sendMessage}
                    dates={dates}
                    guests={guests}
                    bookingDetails={bookingDetails}
                    sendMessageEvent={sendMessageEvent}
                    listingOwnerData={listingOwnerData}
                    userModel={userModel}
                />
            )}
            {isReview && <ReviewPopup listing={listing} reviews={listing.ReviewStayModels?.items || []} toggleSeeReview={toggleSeeReview} isReview={isReview} />}
            {listing && reservervationGuestsMenu && (
                                    <GlobalBookingGuestPicker
                    listing={listing}
                    listingOwnerData={listingOwnerData}
                    toggleGuests={toggleGuests}
                    adults={guests.adults}
                    pets={guests.pets}
                    children={guests.children}
                    infants={guests.infants}
                    updateGuests={updateGuests}
                />
            )}

            {calendarSelected && (
                <DatepickerBooking
                    calendarObjects={calendarObjectsRaw}
                    startDate={dates.startDate}
                    endDate={dates.endDate}
                    listing={listing}
                    cancelFunc={() => {
                        toggleCalendar();
                        if (sendAMessageBtnClicked) {
                            toggleSendAMessageBtnClicked();
                        }
                    }}
                    saveFunc={async (dateRange: Date[]) => {
                        await updateDates(dateRange);
                        if (sendAMessageBtnClicked) {
                            toggleSendMessageRequest();
                            toggleSendAMessageBtnClicked();
                        }
                    }}
                    handleClickOutside={() => {
                        toggleCalendar();
                        if (sendAMessageBtnClicked) {
                            toggleSendAMessageBtnClicked();
                        }
                    }}
                />
            )}
            {shareListingSelected && <ShareListingPopup toggleShareListing={toggleShareListing} listing={listing} selected={shareListingSelected} />}

            {lightboxOpen && (
                <ViewListingLightbox
                    images={parsedImages}
                    initialIndex={photoIndex}
                    onClose={() => setLightboxOpen(false)}
                    listingTitle={listing?.propertyTitle || ''}
                    listingId={listing?.id || ''}
                    hotelRooms={listing?.hotelRooms}
                    propertyDetails={{
                        guests: listing?.guests || 0,
                        bedrooms: listing?.bedrooms || 0,
                        beds: listing?.beds || 0,
                        baths: listing?.bathrooms || 0,
                        area: listing?.areaValue || 0
                    }}
                />
            )}
        </>
    );
}
